import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
export default function Login() {
  const navigate = useNavigate();
  const { login, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState(() => {
    // Pre-fill email if user previously checked "remember me"
    const savedEmail = localStorage.getItem('hrms-remembered-email') || '';
    return { email: savedEmail, password: '', remember: !!savedEmail };
  });

  // Forgot password state
  const [forgotStep, setForgotStep] = useState(null); // null, 'email', 'otp', 'done'
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const checkExistingAuth = async () => {
      const user = await checkAuth();
      if (user) navigate('/dashboard', { replace: true });
    };
    checkExistingAuth();
  }, [checkAuth, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      // Persist email if "remember me" is checked (password is handled by the browser's password manager via autoComplete attrs)
      if (loginForm.remember) {
        localStorage.setItem('hrms-remembered-email', loginForm.email);
      } else {
        localStorage.removeItem('hrms-remembered-email');
      }
      toast.success('Login successful!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmitEmail = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('If this email is registered, a 6-digit code has been sent. Please check your inbox.');
      setForgotStep('otp');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || !newPassword) { toast.error('Please fill all fields'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, otp, new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      toast.success(data.message);
      setForgotStep('done');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const resetForgotFlow = () => {
    setForgotStep(null);
    setForgotEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // ========== FORGOT PASSWORD SCREENS ==========
  const renderForgotPassword = () => {
    if (forgotStep === 'done') {
      return (
        <Card className="border-slate-200 dark:border-slate-700 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <ShieldCheck className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Password Reset Successful</h3>
            <p className="text-sm text-slate-500">You can now login with your new password.</p>
            <Button className="btn-primary w-full" onClick={resetForgotFlow} data-testid="back-to-login-btn">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (forgotStep === 'otp') {
      return (
        <Card className="border-slate-200 dark:border-slate-700 shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-bold text-slate-900 dark:text-white">Verify & Reset</CardTitle>
            <CardDescription>Enter the 6-digit code sent to <span className="font-medium text-slate-700">{forgotEmail}</span></CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-center">
              <p className="text-xs text-blue-700">Check your email inbox (and spam folder) for the 6-digit verification code. Code expires in 10 minutes.</p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Verification Code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="text" maxLength={6} placeholder="Enter 6-digit code" className="pl-10 text-center text-lg tracking-widest font-mono"
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required data-testid="otp-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" placeholder="Enter new password (min 6 chars)" className="pl-10"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="new-password-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" placeholder="Confirm new password" className="pl-10"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-11" disabled={loading} data-testid="reset-password-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reset Password
              </Button>
              <Button type="button" variant="ghost" className="w-full text-sm text-slate-500" onClick={resetForgotFlow}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      );
    }

    // Step 1: Enter email
    return (
      <Card className="border-slate-200 dark:border-slate-700 shadow-lg">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl font-bold text-slate-900 dark:text-white">Forgot Password</CardTitle>
          <CardDescription>Enter your registered email to receive a verification code</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleForgotSubmitEmail} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="email" placeholder="Enter your email" className="pl-10"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary h-11" disabled={loading} data-testid="send-otp-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Verification Code
            </Button>
            <Button type="button" variant="ghost" className="w-full text-sm text-slate-500" onClick={resetForgotFlow}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626]/20 to-transparent" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#DC2626]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#DC2626]/5 rounded-full translate-y-1/3 -translate-x-1/3" />
        
        <div className="relative z-10 text-white max-w-lg">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-white rounded-md flex items-center justify-center p-1.5 shrink-0">
              <img src="/sgi-logo.jpg" alt="SGI" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-4xl font-black tracking-tight">SGI HRMS</h1>
              <p className="text-slate-400 text-sm">Campus Management System</p>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold mb-4">Human Resource Management System</h2>
          <p className="text-slate-300 leading-relaxed mb-6">
            Centralized HR management for multi-institute educational campus. 
            Manage attendance, payroll, leaves, performance, and compliance across all entities.
          </p>
          
          <div className="space-y-3">
            {['Multi-Entity Management', 'Face + GPS Attendance', 'Automated Payroll', 'Leave Workflow', 'Performance Tracking'].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#DC2626]" />
                <span className="text-slate-300 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login / Forgot Password */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-8 bg-slate-50 dark:bg-slate-900 min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white rounded-md flex items-center justify-center p-1 shrink-0 border border-slate-200 dark:border-slate-700">
              <img src="/sgi-logo.jpg" alt="SGI" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">SGI HRMS</h1>
            </div>
          </div>

          {forgotStep !== null ? renderForgotPassword() : (
            <Card className="border-slate-200 dark:border-slate-700 shadow-lg">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Welcome Back</CardTitle>
                <CardDescription>Sign in to access your HR dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4" autoComplete="on" name="loginForm" method="POST" action="#">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="login-email" type="email" name="email" placeholder="Enter your email" className="pl-10"
                        autoComplete="username" autoCapitalize="off" autoCorrect="off" spellCheck="false" inputMode="email"
                        value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required data-testid="login-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button type="button" className="text-xs text-[#DC2626] hover:underline font-medium"
                        onClick={() => setForgotStep('email')} data-testid="forgot-password-link">
                        Forgot Password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="login-password" type={showPassword ? 'text' : 'password'} name="password" placeholder="Enter your password" className="pl-10 pr-10"
                        autoComplete="current-password"
                        value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required data-testid="login-password-input" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword(!showPassword)} tabIndex={-1} aria-label="Toggle password visibility">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={loginForm.remember}
                      onChange={(e) => setLoginForm({ ...loginForm, remember: e.target.checked })}
                      className="rounded border-slate-300 text-[#DC2626] focus:ring-[#DC2626]"
                      data-testid="remember-me-checkbox"
                    />
                    <span>Keep me signed in on this device for 30 days</span>
                  </label>

                  <Button type="submit" className="w-full btn-primary h-11 text-base" disabled={loading} data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
                <p className="text-center text-xs text-slate-400 mt-6">
                  Contact your HR administrator if you need access.
                </p>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-xs text-slate-500 mt-6">
            &copy; {new Date().getFullYear()} Sanjay Ghodawat Institute. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
